<script setup lang="ts">
import { Amplitude } from '@seede/amplitude';
import { t } from '~/i18n';

const email = ref('');
const code = ref('');
const newPassword = ref('');
const confirmPassword = ref('');
const error = ref('');
const isLoading = ref(false);
const isCodeSent = ref(false);
const isSuccess = ref(false);
const showPassword = ref(false);
const countdown = ref(0);
let timer: any = null;
const hasTrackedSendCodeStart = ref(false);
const hasTrackedSendCodeSuccess = ref(false);
const hasTrackedResetSuccess = ref(false);

const { forgotPassword, resetPassword } = useAuth();

definePageMeta({
  layout: 'auth',
});

const togglePasswordVisibility = () => {
  showPassword.value = !showPassword.value;
};

// SEO: 重置密码页面 noindex（同 login / signup）。
useHead({
  title: () => t('landingPages.auth.forgot.seoDocumentTitle'),
  meta: [
    { charset: 'utf-8' },
    { name: 'viewport', content: 'width=device-width, initial-scale=1' },
    { name: 'robots', content: 'noindex, nofollow' },
    { name: 'title', content: () => t('landingPages.auth.forgot.seoTitle') },
    { name: 'description', content: () => t('landingPages.auth.forgot.seoDescription') },
  ],
});

const handleSendCode = async () => {
  error.value = '';
  if (!email.value) {
    error.value = t('landingPages.auth.common.emailRequired');
    return;
  }

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email.value)) {
    error.value = t('landingPages.auth.common.emailInvalid');
    return;
  }

  if (!hasTrackedSendCodeStart.value) {
    Amplitude.event('password_reset', {
      step: 'requested',
    });
    hasTrackedSendCodeStart.value = true;
  }

  isLoading.value = true;
  try {
    await forgotPassword({ email: email.value });
    if (!hasTrackedSendCodeSuccess.value) {
      Amplitude.event('verification_sent', {
        method: 'email',
        scenario: 'forgot_password',
      });
      hasTrackedSendCodeSuccess.value = true;
    }
    isCodeSent.value = true;
    countdown.value = 60;
    timer = setInterval(() => {
      countdown.value--;
      if (countdown.value <= 0) {
        clearInterval(timer);
        timer = null;
      }
    }, 1000);
  } catch (err: any) {
    error.value = err.data?.message || err.message || t('landingPages.auth.common.sendCodeFailed');
  } finally {
    isLoading.value = false;
  }
};

const handleSubmit = async () => {
  error.value = '';

  if (!isCodeSent.value) {
    await handleSendCode();
    return;
  }

  if (!code.value) {
    error.value = t('landingPages.auth.common.verificationCodeRequired');
    return;
  }

  if (!newPassword.value) {
    error.value = t('landingPages.auth.forgot.newPasswordRequired');
    return;
  }

  if (newPassword.value.length < 6) {
    error.value = t('landingPages.auth.common.passwordMinLength');
    return;
  }

  if (newPassword.value !== confirmPassword.value) {
    error.value = t('landingPages.auth.forgot.passwordsMismatch');
    return;
  }

  isLoading.value = true;
  try {
    await resetPassword({
      email: email.value,
      code: code.value,
      newPassword: newPassword.value,
    });
    if (!hasTrackedResetSuccess.value) {
      Amplitude.event('password_reset', {
        step: 'completed',
      });
      hasTrackedResetSuccess.value = true;
    }
    isSuccess.value = true;
  } catch (err: any) {
    error.value = err.data?.message || err.message || t('landingPages.auth.forgot.resetFailed');
  } finally {
    isLoading.value = false;
  }
};

onUnmounted(() => {
  if (timer) clearInterval(timer);
});
</script>

<template>
  <!-- Main Content -->
  <main class="flex-1 flex flex-col justify-center items-center relative z-10 px-4 py-8">
    <!-- Form Container -->
    <div
      class="w-full max-w-[400px] mx-auto panel-gradient rounded-3xl px-6 pt-4 pb-6 shadow-lg backdrop-blur-[20px]"
    >
      <div class="flex items-center justify-between mb-8 md:mb-12">
        <h1 class="text-2xl md:text-3xl font-semibold tracking-tight text-black/90">
          {{
            isSuccess
              ? $t('landingPages.auth.forgot.titleSuccess')
              : isCodeSent
                ? $t('landingPages.auth.forgot.titleCodeSent')
                : $t('landingPages.auth.forgot.titleInitial')
          }}
        </h1>
        <NuxtLink to="/" class="inline-block transition-opacity duration-300">
          <img src="/veesoai-condense-dark.svg" alt="Veeso AI" class="h-8 w-auto" />
        </NuxtLink>
      </div>

      <!-- Success State -->
      <div v-if="isSuccess" class="flex flex-col gap-6">
        <p class="text-base font-normal text-black/60">
          {{ $t('landingPages.auth.forgot.successMessage') }}
        </p>
        <NuxtLink
          to="/login"
          class="w-full h-10 px-4 flex items-center justify-center text-base font-normal text-black/90 bg-transparent border border-black/10 rounded-lg transition-all duration-200 tracking-[0.02em] hover:bg-black/5"
        >
          {{ $t('landingPages.auth.forgot.backToLogin') }}
        </NuxtLink>
      </div>

      <!-- Form State -->
      <form v-else @submit.prevent="handleSubmit" class="flex flex-col gap-3">
        <!-- Email Input (Disabled after code sent) -->
        <input
          id="email"
          v-model="email"
          type="email"
          :disabled="isCodeSent || isLoading"
          class="w-full h-10 px-4 text-base font-normal text-black/90 bg-transparent border border-black/10 rounded-lg transition-all duration-200 outline-none placeholder:text-black/40 disabled:opacity-50"
          :placeholder="$t('landingPages.auth.common.email')"
          required
          autocomplete="email"
          @input="error = ''"
        />

        <template v-if="isCodeSent">
          <!-- Verification Code -->
          <div class="relative flex gap-2">
            <input
              id="code"
              v-model="code"
              type="text"
              class="flex-1 h-10 px-4 text-base font-normal text-black/90 bg-transparent border border-black/10 rounded-lg transition-all duration-200 outline-none placeholder:text-black/40"
              :placeholder="$t('landingPages.auth.common.verificationCode')"
              required
              @input="error = ''"
            />
            <button
              type="button"
              @click="handleSendCode"
              :disabled="countdown > 0 || isLoading"
              class="h-10 px-4 text-sm whitespace-nowrap font-normal bg-black/5 text-black/90 rounded-lg transition-all duration-200 hover:bg-black/10 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {{
                countdown > 0
                  ? $t('landingPages.auth.common.countdownSeconds', { count: countdown })
                  : $t('landingPages.auth.forgot.resend')
              }}
            </button>
          </div>

          <!-- New Password -->
          <div class="relative border border-black/10 rounded-lg">
            <input
              id="new-password"
              v-model="newPassword"
              :type="showPassword ? 'text' : 'password'"
              class="w-full h-10 px-4 text-base font-normal text-black/90 bg-transparent transition-all duration-200 outline-none placeholder:text-black/40"
              :placeholder="$t('landingPages.auth.forgot.newPassword')"
              required
              autocomplete="new-password"
              minlength="6"
              @input="error = ''"
            />
            <button
              type="button"
              @click="togglePasswordVisibility"
              class="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-black/40 hover:text-black/60 transition-colors duration-200"
              tabindex="-1"
            >
              <svg
                v-if="!showPassword"
                class="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                />
              </svg>
              <svg v-else class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.29 3.29m0 0L12 12m-5.71-5.71L3 3m9 9l5.71 5.71M21 21l-5.71-5.71"
                />
              </svg>
            </button>
          </div>

          <!-- Confirm Password -->
          <input
            id="confirm-password"
            v-model="confirmPassword"
            type="password"
            class="w-full h-10 px-4 text-base font-normal text-black/90 bg-transparent border border-black/10 rounded-lg transition-all duration-200 outline-none placeholder:text-black/40"
            :placeholder="$t('landingPages.auth.forgot.confirmPassword')"
            required
            autocomplete="new-password"
            @input="error = ''"
          />
        </template>

        <!-- Submit Button -->
        <button
          type="submit"
          class="w-full h-10 px-4 flex items-center justify-center text-base font-normal text-black/90 bg-transparent border border-black/10 rounded-lg transition-all duration-200 tracking-[0.02em] disabled:opacity-50 disabled:cursor-not-allowed hover:bg-black/5"
          :disabled="isLoading"
        >
          {{
            isLoading
              ? isCodeSent
                ? $t('landingPages.auth.forgot.submittingReset')
                : $t('landingPages.auth.forgot.submittingSend')
              : isCodeSent
                ? $t('landingPages.auth.forgot.submitReset')
                : $t('landingPages.auth.common.sendCode')
          }}
        </button>

        <!-- Error Message -->
        <div
          v-if="error"
          class="px-4 py-2.5 text-sm font-normal text-[#f90000] bg-[#f90000]/5 rounded-lg"
        >
          {{ error }}
        </div>

        <!-- Back Link -->
        <div class="text-left text-sm font-normal text-black/80 mt-2">
          {{ $t('landingPages.auth.forgot.rememberPassword') }}
          <NuxtLink
            to="/login"
            class="text-black/80 underline ml-1 transition-colors hover:text-black"
          >
            {{ $t('landingPages.auth.common.logIn') }}
          </NuxtLink>
        </div>
      </form>
    </div>

    <!-- Terms Agreement Text -->
    <div class="mt-6 text-center text-xs font-normal text-white/30 px-4">
      {{ $t('landingPages.auth.common.termsPrefix') }}
      <NuxtLink to="/terms" class="text-white/30 underline transition-colors hover:text-white">
        {{ $t('landingPages.auth.common.termsOfService') }}
      </NuxtLink>
      {{ $t('landingPages.auth.common.and') }}
      <NuxtLink to="/privacy" class="text-white/30 underline transition-colors hover:text-white">
        {{ $t('landingPages.auth.common.privacyPolicy') }}
      </NuxtLink>
      .
    </div>
  </main>
</template>

<style scoped>
.panel-gradient {
  background: linear-gradient(
    to bottom,
    rgba(255, 255, 255, 0.6) 0%,
    rgba(255, 255, 255, 1) 20%,
    rgba(255, 255, 255, 1) 100%
  );
}

/* Prevent zoom on iOS */
@media (max-width: 640px) {
  input {
    font-size: 16px;
  }
}

/* Disable browser autofill styles */
input:-webkit-autofill,
input:-webkit-autofill:hover,
input:-webkit-autofill:focus,
input:-webkit-autofill:active {
  -webkit-box-shadow: 0 0 0 30px transparent inset !important;
  box-shadow: 0 0 0 30px transparent inset !important;
  transition: background-color 5000s ease-in-out 0s;
}
</style>
